import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Space, Button, Table, Tag, Modal, Form, Input, Select,
  DatePicker, Row, Col, Progress, Upload, message, Popconfirm, Descriptions,
  Badge, Alert, Statistic, Drawer, Tabs, Dropdown, Menu
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined,
  TrophyOutlined, UploadOutlined, EyeOutlined, DownloadOutlined,
  FilterOutlined, HistoryOutlined, BookOutlined, StarOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPrimarySchoolId,
  normalizeScopeId,
  resolveDefaultSchoolId,
  resolveOperationSchoolId,
  shouldLockSchoolSelection
} from '../../utils/schoolScope';
import studentAPI from '../../services/studentAPI';
import api from '../../services/api';
import moment from 'moment';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const StudentGrades = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(getPrimarySchoolId(user));
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [studentDetailVisible, setStudentDetailVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentGrades, setStudentGrades] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [classGradeStats, setClassGradeStats] = useState(null);
  const [subjectStats, setSubjectStats] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [examList, setExamList] = useState([]);
  const [filterForm] = Form.useForm();
  const [subjectFullScores, setSubjectFullScores] = useState({}); // 存储各科满分
  const [originalSubjectScores, setOriginalSubjectScores] = useState({}); // 存储各科原始分数数组
  const [rateDetailVisible, setRateDetailVisible] = useState(false); // 三率详情抽屉
  const [rateDetailData, setRateDetailData] = useState({ type: '', students: [] }); // 三率详情数据
  const [gradeLevelConfigVisible, setGradeLevelConfigVisible] = useState(false); // 等级转换配置模态框
  const [gradeLevelConfigForm] = Form.useForm();
  const [gradeLevelConfig, setGradeLevelConfig] = useState(null); // 等级转换配置
  const [gradeLevelType, setGradeLevelType] = useState('score'); // 等级类型: 'score' 或 'rank'
  const [gradeLevelDetailVisible, setGradeLevelDetailVisible] = useState(false); // 等级详情抽屉
  const [gradeLevelDetailData, setGradeLevelDetailData] = useState({ level: '', students: [] }); // 等级详情数据
  const [currentLevelStats, setCurrentLevelStats] = useState(null); // 当前选中科目的等级统计
  const [levelStatsLoading, setLevelStatsLoading] = useState(false); // 等级统计加载状态

  // 科目选项
  const subjects = [
    { value: '语文', label: '语文', color: 'red' },
    { value: '数学', label: '数学', color: 'blue' },
    { value: '英语', label: '英语', color: 'green' },
    { value: '物理', label: '物理', color: 'purple' },
    { value: '化学', label: '化学', color: 'orange' },
    { value: '生物', label: '生物', color: 'cyan' },
    { value: '历史', label: '历史', color: 'gold' },
    { value: '地理', label: '地理', color: 'lime' },
    { value: '政治', label: '政治', color: 'magenta' },
    { value: '体育', label: '体育', color: 'volcano' },
    { value: '音乐', label: '音乐', color: 'geekblue' },
    { value: '美术', label: '美术', color: 'pink' },
  ];

  // 考试类型选项
  const examTypes = [
    { value: '期中考试', label: '期中考试', color: 'blue' },
    { value: '期末考试', label: '期末考试', color: 'red' },
    { value: '月考', label: '月考', color: 'green' },
    { value: '单元测试', label: '单元测试', color: 'orange' },
    { value: '随堂测验', label: '随堂测验', color: 'purple' },
    { value: '模拟考试', label: '模拟考试', color: 'cyan' },
  ];

  // 获取班级成绩统计
  const fetchClassStats = async (classId, examType, examName) => {
    if (!classId || !examType || !examName) return;

    try {
      const response = await api.get('/student-grades/class-stats', {
        params: { classId, examType, examName }
      });
      if (response.data.success) {
        setClassGradeStats(response.data.data);
      }
    } catch (error) {
      console.error('获取班级统计失败:', error);
    }
  };

  // 获取班级各科成绩统计
  const fetchSubjectStats = async (classId, examType, examName) => {
    if (!classId || !examType || !examName) {
      setSubjectStats([]);
      setSelectedSubject(null);
      return;
    }

    try {
      const response = await api.get('/student-grades/subject-stats', {
        params: { classId, examType, examName }
      });
      if (response.data.success) {
        const stats = response.data.data || [];
        setSubjectStats(stats);

        // 初始化满分设置和保存原始分数
        const fullScores = {};
        const originalScores = {};
        stats.forEach(stat => {
          if (stat.fullScore) {
            // 包括总分在内的所有科目都保存满分
            fullScores[stat.field] = stat.fullScore;
            if (stat.field !== 'total') {
              originalScores[stat.field] = stat.scores || []; // 保存原始分数数组
            }
          }
        });
        setSubjectFullScores(fullScores);
        setOriginalSubjectScores(originalScores);

        // 默认选择第一个科目(总分)
        if (stats.length > 0 && !selectedSubject) {
          setSelectedSubject(stats[0]);
        }
      }
    } catch (error) {
      console.error('获取各科统计失败:', error);
      setSubjectStats([]);
    }
  };

  // 重新计算某个科目的三率
  const recalculateSubjectStats = (field, newFullScore) => {
    const scores = originalSubjectScores[field];
    if (!scores || scores.length === 0) return null;

    // 基于新满分重新计算三率
    const excellentCount = scores.filter(s => (s / newFullScore) >= 0.8).length;
    const goodCount = scores.filter(s => (s / newFullScore) >= 0.7 && (s / newFullScore) < 0.8).length;
    const passCount = scores.filter(s => (s / newFullScore) >= 0.6).length;

    const excellentRate = (excellentCount / scores.length) * 100;
    const goodRate = (goodCount / scores.length) * 100;
    const passRate = (passCount / scores.length) * 100;

    return {
      excellentRate: parseFloat(excellentRate.toFixed(1)),
      goodRate: parseFloat(goodRate.toFixed(1)),
      passRate: parseFloat(passRate.toFixed(1)),
      excellentCount,
      goodCount,
      passCount
    };
  };

  // 处理满分变化,重新计算三率并保存到数据库
  const handleFullScoreChange = async (field, newFullScore) => {
    const score = parseFloat(newFullScore);
    if (isNaN(score) || score <= 0) {
      message.error('满分必须是大于0的数字');
      return;
    }

    // 更新满分
    setSubjectFullScores(prev => ({
      ...prev,
      [field]: score
    }));

    // 重新计算三率
    const newStats = recalculateSubjectStats(field, score);
    if (newStats) {
      setSubjectStats(prevStats => {
        return prevStats.map(stat => {
          if (stat.field === field) {
            return {
              ...stat,
              fullScore: score,
              ...newStats
            };
          }
          return stat;
        });
      });

      // 如果当前选中的是这个科目,更新显示
      if (selectedSubject && selectedSubject.field === field) {
        setSelectedSubject(prev => ({
          ...prev,
          fullScore: score,
          ...newStats
        }));
      }

      // 保存满分配置到数据库
      if (filters.classId && filters.examType && filters.examName) {
        try {
          // 获取考试日期（从成绩记录中获取）
          const examDateResponse = await api.get('/student-grades/multi', {
            params: {
              classId: filters.classId,
              examType: filters.examType,
              examName: filters.examName,
              pageSize: 1
            }
          });

          let examDate = new Date().toISOString().split('T')[0]; // 默认今天
          if (examDateResponse.data.success && examDateResponse.data.data.length > 0) {
            const rawDate = examDateResponse.data.data[0].exam_date;
            // 处理ISO日期格式，提取YYYY-MM-DD部分
            if (rawDate) {
              if (rawDate.includes('T')) {
                examDate = rawDate.split('T')[0];
              } else if (rawDate.includes(' ')) {
                examDate = rawDate.split(' ')[0];
              } else {
                examDate = rawDate;
              }
            }
            console.log('从成绩记录获取的考试日期:', rawDate, '-> 格式化后:', examDate);
          }

          // 准备所有科目的满分数据
          const fullScores = {};
          subjectStats.forEach(stat => {
            if (stat.field !== 'total') {
              fullScores[stat.field] = stat.field === field ? score : (subjectFullScores[stat.field] || stat.fullScore || 100);
            }
          });

          console.log('保存满分配置:', {
            classId: filters.classId,
            examType: filters.examType,
            examName: filters.examName,
            examDate: examDate,
            fullScores: fullScores
          });

          const response = await api.post('/student-grades/full-score-config', {
            classId: filters.classId,
            examType: filters.examType,
            examName: filters.examName,
            examDate: examDate,
            fullScores: fullScores
          });

          console.log('保存满分配置响应:', response.data);

          if (response.data.success) {
            message.success('满分已保存到数据库');
          } else {
            message.warning('保存满分失败: ' + (response.data.message || '未知错误'));
          }
        } catch (error) {
          console.error('保存满分配置失败:', error);
          message.error('满分保存失败: ' + (error.response?.data?.message || error.message));
        }
      } else {
        console.warn('缺少必要的筛选条件，无法保存满分配置', {
          classId: filters.classId,
          examType: filters.examType,
          examName: filters.examName
        });
        message.warning('请先选择班级和考试后再设置满分');
      }

      // 重新计算总分的三率
      const totalStat = subjectStats.find(s => s.field === 'total');
      if (totalStat) {
        // 总分的三率依赖所有科目，所以不需要重新计算
      }

      message.success(`满分已更新为 ${score} 分,三率已重新计算`);
    }
  };

  // 重新计算总分的三率
  const recalculateTotalStats = () => {
    // 计算总满分
    const totalFullScore = Object.values(subjectFullScores).reduce((sum, score) => sum + (score || 0), 0);
    if (totalFullScore === 0) return;

    // 获取所有学生的各科分数,计算每个学生的总分和得分率
    const allSubjectScores = originalSubjectScores;
    const subjectFields = Object.keys(allSubjectScores);

    if (subjectFields.length === 0) return;

    // 获取学生数量(从任意一个科目)
    const studentCount = allSubjectScores[subjectFields[0]]?.length || 0;
    if (studentCount === 0) return;

    // 计算每个学生的得分率
    const studentRates = [];
    for (let i = 0; i < studentCount; i++) {
      let studentTotal = 0;
      let studentFullScore = 0;

      subjectFields.forEach(field => {
        const score = allSubjectScores[field]?.[i];
        const fullScore = subjectFullScores[field];
        if (score !== undefined && fullScore !== undefined) {
          studentTotal += score;
          studentFullScore += fullScore;
        }
      });

      if (studentFullScore > 0) {
        const rate = (studentTotal / studentFullScore) * 100;
        studentRates.push(rate);
      }
    }

    // 计算总分三率
    const excellentCount = studentRates.filter(rate => rate >= 80).length;
    const goodCount = studentRates.filter(rate => rate >= 70 && rate < 80).length;
    const passCount = studentRates.filter(rate => rate >= 60).length;

    const excellentRate = (excellentCount / studentRates.length) * 100;
    const goodRate = (goodCount / studentRates.length) * 100;
    const passRate = (passCount / studentRates.length) * 100;

    // 更新总分统计
    setSubjectStats(prevStats => {
      return prevStats.map(stat => {
        if (stat.field === 'total') {
          return {
            ...stat,
            excellentRate: parseFloat(excellentRate.toFixed(1)),
            goodRate: parseFloat(goodRate.toFixed(1)),
            passRate: parseFloat(passRate.toFixed(1)),
            excellentCount,
            goodCount,
            passCount
          };
        }
        return stat;
      });
    });

    // 如果当前选中的是总分,更新显示
    if (selectedSubject && selectedSubject.field === 'total') {
      setSelectedSubject(prev => ({
        ...prev,
        excellentRate: parseFloat(excellentRate.toFixed(1)),
        goodRate: parseFloat(goodRate.toFixed(1)),
        passRate: parseFloat(passRate.toFixed(1)),
        excellentCount,
        goodCount,
        passCount
      }));
    }
  };

  // 加载等级转换配置
  useEffect(() => {
    const savedConfig = localStorage.getItem('gradeLevelConfig');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setGradeLevelConfig(config);
        setGradeLevelType(config.type || 'score');
      } catch (error) {
        console.error('加载等级配置失败:', error);
      }
    }
  }, []);

  // 加载等级统计数据
  useEffect(() => {
    const fetchLevelStats = async () => {
      const classId = filterForm.getFieldValue('classId');
      const examType = filterForm.getFieldValue('examType');
      const examName = filterForm.getFieldValue('examName');

      if (!classId || !examType || !examName || !selectedSubject || !gradeLevelConfig) {
        setCurrentLevelStats(null);
        return;
      }

      setLevelStatsLoading(true);
      try {
        const response = await api.get('/student-grades/multi', {
          params: {
            classId,
            examType,
            examName,
            page: 1,
            pageSize: 1000
          }
        });

        if (response.data.success) {
          const allStudents = response.data.data || [];
          const stats = calculateGradeLevelStats(allStudents, selectedSubject.field);
          setCurrentLevelStats(stats);
        }
      } catch (error) {
        console.error('获取等级统计失败:', error);
        setCurrentLevelStats(null);
      } finally {
        setLevelStatsLoading(false);
      }
    };

    fetchLevelStats();
  }, [selectedSubject, gradeLevelConfig, filters]);

  // 打开等级配置模态框
  const openGradeLevelConfig = () => {
    if (gradeLevelConfig) {
      gradeLevelConfigForm.setFieldsValue({
        type: gradeLevelConfig.type || 'score',
        A_min: gradeLevelConfig.A_min,
        B_min: gradeLevelConfig.B_min,
        C_min: gradeLevelConfig.C_min,
        D_min: gradeLevelConfig.D_min,
        E_min: gradeLevelConfig.E_min,
        A_percent: gradeLevelConfig.A_percent,
        B_percent: gradeLevelConfig.B_percent,
        C_percent: gradeLevelConfig.C_percent,
        D_percent: gradeLevelConfig.D_percent,
        E_percent: gradeLevelConfig.E_percent,
      });
    } else {
      // 默认值
      gradeLevelConfigForm.setFieldsValue({
        type: 'score',
        A_min: 90,
        B_min: 80,
        C_min: 70,
        D_min: 60,
        E_min: 50,
        A_percent: 10,
        B_percent: 20,
        C_percent: 30,
        D_percent: 20,
        E_percent: 15,
      });
    }
    setGradeLevelConfigVisible(true);
  };

  // 保存等级配置
  const handleSaveGradeLevelConfig = (values) => {
    const config = {
      type: values.type,
      A_min: values.A_min,
      B_min: values.B_min,
      C_min: values.C_min,
      D_min: values.D_min,
      E_min: values.E_min,
      A_percent: values.A_percent,
      B_percent: values.B_percent,
      C_percent: values.C_percent,
      D_percent: values.D_percent,
      E_percent: values.E_percent,
    };

    localStorage.setItem('gradeLevelConfig', JSON.stringify(config));
    setGradeLevelConfig(config);
    setGradeLevelType(config.type);
    message.success('等级配置保存成功');
    setGradeLevelConfigVisible(false);
  };

  // 根据分数或排名计算等级
  const calculateGradeLevel = (score, rank, totalStudents, fullScore) => {
    if (!gradeLevelConfig) return { level: '-', color: 'default' };

    if (gradeLevelConfig.type === 'score') {
      // 基于分数的等级划分（使用得分率）
      const percentage = (score / fullScore) * 100;
      if (percentage >= gradeLevelConfig.A_min) return { level: 'A', color: 'red' };
      if (percentage >= gradeLevelConfig.B_min) return { level: 'B', color: 'orange' };
      if (percentage >= gradeLevelConfig.C_min) return { level: 'C', color: 'gold' };
      if (percentage >= gradeLevelConfig.D_min) return { level: 'D', color: 'blue' };
      if (percentage >= gradeLevelConfig.E_min) return { level: 'E', color: 'cyan' };
      return { level: 'F', color: 'gray' };
    } else {
      // 基于排名的等级划分（前百分比）
      if (!rank || !totalStudents) return { level: '-', color: 'default' };
      const rankPercentage = (rank / totalStudents) * 100;
      if (rankPercentage <= gradeLevelConfig.A_percent) return { level: 'A', color: 'red' };
      if (rankPercentage <= gradeLevelConfig.A_percent + gradeLevelConfig.B_percent) return { level: 'B', color: 'orange' };
      if (rankPercentage <= gradeLevelConfig.A_percent + gradeLevelConfig.B_percent + gradeLevelConfig.C_percent) return { level: 'C', color: 'gold' };
      if (rankPercentage <= gradeLevelConfig.A_percent + gradeLevelConfig.B_percent + gradeLevelConfig.C_percent + gradeLevelConfig.D_percent) return { level: 'D', color: 'blue' };
      if (rankPercentage <= gradeLevelConfig.A_percent + gradeLevelConfig.B_percent + gradeLevelConfig.C_percent + gradeLevelConfig.D_percent + gradeLevelConfig.E_percent) return { level: 'E', color: 'cyan' };
      return { level: 'F', color: 'gray' };
    }
  };

  // 获取科目满分（统一处理总分和单科）
  const getSubjectFullScore = (subjectField) => {
    if (subjectField === 'total') {
      // 总分：优先使用后端返回的总分满分，否则计算各科满分之和
      return subjectFullScores['total'] ||
        Object.values(subjectFullScores).reduce((sum, score) => {
          if (typeof score === 'number') return sum + score;
          return sum;
        }, 0) ||
        selectedSubject?.fullScore ||
        100; // 最后的兜底值
    } else {
      // 单科：使用该科目的满分
      return subjectFullScores[subjectField] || selectedSubject?.fullScore || 100;
    }
  };

  // 计算等级统计
  const calculateGradeLevelStats = (students, subjectField) => {
    if (!gradeLevelConfig || !students || students.length === 0) return null;

    const stats = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0 };
    const levelStudents = { A: [], B: [], C: [], D: [], E: [], F: [] };

    // 使用辅助函数获取满分
    const fullScore = getSubjectFullScore(subjectField);

    // 如果满分为0，返回null（无法计算等级）
    if (fullScore === 0) {
      console.warn('满分为0，无法计算等级统计');
      return null;
    }

    students.forEach((student, index) => {
      const score = subjectField === 'total'
        ? parseFloat(student.total_score || 0)
        : parseFloat(student[subjectField] || 0);

      const rank = student.class_rank || (index + 1);
      const { level } = calculateGradeLevel(score, rank, students.length, fullScore);

      if (level !== '-') {
        stats[level]++;
        levelStudents[level].push({
          ...student,
          score: score,
          rank: rank
        });
      }
    });

    return {
      stats,
      levelStudents,
      totalStudents: students.length
    };
  };

  // 查看等级详情
  const handleViewGradeLevelDetail = async (level) => {
    const classId = filterForm.getFieldValue('classId');
    const examType = filterForm.getFieldValue('examType');
    const examName = filterForm.getFieldValue('examName');

    if (!classId || !examType || !examName || !selectedSubject) {
      message.error('请先选择班级和考试');
      return;
    }

    try {
      const response = await api.get('/student-grades/multi', {
        params: {
          classId,
          examType,
          examName,
          page: 1,
          pageSize: 1000
        }
      });

      if (response.data.success) {
        const allStudents = response.data.data || [];
        const levelStats = calculateGradeLevelStats(allStudents, selectedSubject.field);

        if (levelStats && levelStats.levelStudents[level]) {
          const fullScore = getSubjectFullScore(selectedSubject.field);

          const students = levelStats.levelStudents[level].map(student => {
            const percentage = (student.score / fullScore) * 100;

            return {
              ...student,
              displayScore: student.score.toFixed(1),
              percentage: percentage.toFixed(1),
            };
          }).sort((a, b) => b.score - a.score);

          setGradeLevelDetailData({
            level,
            students,
            subject: selectedSubject.subject,
            fullScore: fullScore
          });
          setGradeLevelDetailVisible(true);
        }
      }
    } catch (error) {
      console.error('获取等级详情失败:', error);
      message.error('获取等级详情失败');
    }
  };

  // 查看三率详情
  const handleViewRateDetail = async (rateType) => {
    const classId = filterForm.getFieldValue('classId');
    const examType = filterForm.getFieldValue('examType');
    const examName = filterForm.getFieldValue('examName');

    if (!classId || !examType || !examName || !selectedSubject) {
      message.error('请先选择班级和考试');
      return;
    }

    try {
      // 获取该班级该次考试的所有学生成绩
      const response = await api.get('/student-grades/multi', {
        params: {
          classId,
          examType,
          examName,
          page: 1,
          pageSize: 1000
        }
      });

      if (response.data.success) {
        const allStudents = response.data.data || [];
        let filteredStudents = [];

        // 使用辅助函数获取满分
        const fullScore = getSubjectFullScore(selectedSubject.field);

        if (selectedSubject.field === 'total') {
          // 总分的筛选逻辑
          filteredStudents = allStudents.map(student => {
            const rate = (parseFloat(student.total_score) / fullScore) * 100;
            return {
              ...student,
              rate: rate.toFixed(1),
              displayScore: parseFloat(student.total_score).toFixed(1)
            };
          }).filter(student => {
            const rate = parseFloat(student.rate);
            if (rateType === 'excellent') return rate >= 80;
            if (rateType === 'good') return rate >= 70 && rate < 80;
            if (rateType === 'pass') return rate >= 60;
            return false;
          });
        } else {
          // 单科的筛选逻辑
          filteredStudents = allStudents.filter(student => {
            const score = parseFloat(student[selectedSubject.field]);
            return !isNaN(score) && score >= 0;
          }).map(student => {
            const score = parseFloat(student[selectedSubject.field]);
            const rate = (score / fullScore) * 100;
            return {
              ...student,
              rate: rate.toFixed(1),
              displayScore: score.toFixed(1)
            };
          }).filter(student => {
            const rate = parseFloat(student.rate);
            if (rateType === 'excellent') return rate >= 80;
            if (rateType === 'good') return rate >= 70 && rate < 80;
            if (rateType === 'pass') return rate >= 60;
            return false;
          });
        }

        // 按得分率降序排列
        filteredStudents.sort((a, b) => parseFloat(b.rate) - parseFloat(a.rate));

        setRateDetailData({
          type: rateType,
          students: filteredStudents,
          subject: selectedSubject.subject,
          fullScore: fullScore
        });
        setRateDetailVisible(true);
      }
    } catch (error) {
      console.error('获取学生详情失败:', error);
      message.error('获取学生详情失败');
    }
  };

  // 获取班级的考试列表
  const fetchExamList = async (classId) => {
    if (!classId) {
      setExamList([]);
      return;
    }

    try {
      const response = await api.get('/student-grades/multi', {
        params: {
          classId,
          schoolId: selectedSchoolId || getPrimarySchoolId(user),
          page: 1,
          pageSize: 1000  // 获取所有记录
        }
      });

      if (response.data.success) {
        const records = response.data.data || [];
        // 提取唯一的考试类型和考试名称组合
        const examSet = new Map();
        records.forEach(record => {
          if (record.exam_type && record.exam_name) {
            const key = `${record.exam_type}_${record.exam_name}`;
            if (!examSet.has(key)) {
              examSet.set(key, {
                examType: record.exam_type,
                examName: record.exam_name,
                examDate: record.exam_date
              });
            }
          }
        });

        // 转换为数组并按日期排序
        const exams = Array.from(examSet.values()).sort((a, b) => {
          return new Date(b.examDate) - new Date(a.examDate);
        });

        setExamList(exams);
      }
    } catch (error) {
      console.error('获取考试列表失败:', error);
      setExamList([]);
    }
  };

  // 获取成绩记录列表 (多科目版本)
  const fetchGrades = async (params = {}) => {
    setLoading(true);
    try {
      const requestParams = {
        ...filters,
        page: pagination.current,
        pageSize: pagination.pageSize,
        schoolId: selectedSchoolId || getPrimarySchoolId(user),
        ...params
      };

      // 添加排序参数
      if (sortField && sortOrder) {
        requestParams.sortField = sortField;
        requestParams.sortOrder = sortOrder === 'ascend' ? 'ASC' : 'DESC';
      }

      console.log('请求参数:', requestParams);

      const response = await api.get('/student-grades/multi', {
        params: requestParams
      });

      if (response.data.success) {
        setGrades(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      }
    } catch (error) {
      message.error('获取成绩记录失败');
      console.error('获取成绩记录失败:', error);
      setGrades([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取学校列表
  const fetchSchools = async () => {
    try {
      const response = await api.get('/schools');
      if (response.data.success) {
        const rows = response.data.data || [];
        const availableSchools = user?.workbenchSchoolId ? rows.filter(s => String(s.id) === user.workbenchSchoolId) : rows;
        setSchools(availableSchools);
        const defaultSchoolId = resolveDefaultSchoolId(user, availableSchools);
        if (defaultSchoolId) setSelectedSchoolId(defaultSchoolId);
      }
    } catch (error) {
      console.error('获取学校列表失败:', error);
      setSchools([]);
    }
  };

  // 获取学生列表 (已重构为基于班级按需获取，此处仅清理列表)
  const fetchStudents = async () => {
    setStudents([]);
  };

  // 新增：专用于编辑模态框拉取班级学生的辅助方法
  const fetchClassStudentsForModal = async (classId) => {
    if (!classId) {
      setFilteredStudents([]);
      return;
    }
    try {
      const response = await api.get('/students', {
        params: { classId }
      });
      if (response.data.success) {
        setFilteredStudents(response.data.data || []);
      } else {
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error('获取班级学生失败:', error);
      setFilteredStudents([]);
    }
  };

  // 新增：专用于查询筛选表单拉取班级学生的辅助方法
  const fetchClassStudentsForFilter = async (classId) => {
    if (!classId) {
      setStudents([]);
      return;
    }
    try {
      const response = await api.get('/students', {
        params: { classId }
      });
      if (response.data.success) {
        setStudents(response.data.data || []);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error('获取筛选学生失败:', error);
      setStudents([]);
    }
  };

  // 获取班级列表
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes', {
        params: { schoolId: selectedSchoolId || getPrimarySchoolId(user) }
      });
      if (response.data.success) {
        setClasses(response.data.data || []);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      setClasses([]);
    }
  };

  // 处理学校变化
  const handleSchoolChange = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setSelectedClassId(null);
    setFilters({});
  };

  // 根据班级ID筛选学生 (编辑模态框)
  const handleClassChange = (classId) => {
    setSelectedClassId(classId);
    fetchClassStudentsForModal(classId);
    form.setFieldsValue({ student_id: undefined, student_name: undefined });
  };

  // 获取学生详细成绩
  const fetchStudentGrades = async (studentId) => {
    try {
      const student = students.find((item) => String(item.id) === String(studentId));
      const response = await studentAPI.grades.getGrades({
        studentId,
        schoolId: resolveOperationSchoolId({
          classes,
          classId: student?.class_id || student?.classId || selectedClassId,
          item: student,
          selectedSchoolId,
          user
        })
      });
      if (response.data.success) {
        setStudentGrades(response.data.data || []);
      }
    } catch (error) {
      console.error('获取学生成绩失败:', error);
      setStudentGrades([]);
    }
  };

  useEffect(() => {
    fetchSchools();
    fetchGrades();
    fetchStudents();
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchGrades();
    fetchStudents();
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  useEffect(() => {
    fetchGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // 监听分页和排序变化
  useEffect(() => {
    fetchGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, sortField, sortOrder]);

  // 处理表格变化（包括分页和排序）
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    console.log('表格变化:', { newPagination, sorter });

    // 更新分页
    setPagination(newPagination);

    // 更新排序
    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  // 处理筛选
  const handleFilter = (values) => {
    setFilters(values);
    setPagination(prev => ({ ...prev, current: 1 }));

    // 当筛选条件包含班级、考试类型和考试名称时，获取各科统计
    if (values.classId && values.examType && values.examName) {
      fetchSubjectStats(values.classId, values.examType, values.examName);
      fetchClassStats(values.classId, values.examType, values.examName);
    } else {
      setSubjectStats([]);
      setSelectedSubject(null);
      setClassGradeStats(null);
    }
  };

  // 处理筛选表单中的班级选择变化
  const handleFilterClassChange = (classId) => {
    filterForm.setFieldsValue({
      examType: undefined,
      examName: undefined,
      exam: undefined,
      studentId: undefined // 班级变化时同步清空学生选择
    });
    setExamList([]);
    setSubjectStats([]);
    setSelectedSubject(null);
    setClassGradeStats(null);

    if (classId) {
      // 更新filters，保留classId
      setFilters({ classId: classId });
      fetchExamList(classId);
      fetchClassStudentsForFilter(classId); // 按需拉取当前班级学生以供筛选
    } else {
      // 清空filters
      setFilters({});
      setStudents([]); // 清空学生下拉菜单
    }
  };

  // 处理考试选择变化
  const handleExamChange = (value) => {
    if (value) {
      const exam = examList.find(e => `${e.examType}_${e.examName}` === value);
      if (exam) {
        filterForm.setFieldsValue({
          examType: exam.examType,
          examName: exam.examName
        });

        // 获取班级ID
        const classId = filterForm.getFieldValue('classId');

        // 更新filters状态（重要！用于保存满分配置）
        if (classId && exam.examType && exam.examName) {
          setFilters({
            classId: classId,
            examType: exam.examType,
            examName: exam.examName
          });

          // 自动加载统计数据
          fetchSubjectStats(classId, exam.examType, exam.examName);
          fetchClassStats(classId, exam.examType, exam.examName);
        }
      }
    } else {
      // 清空统计数据和filters
      setFilters({});
      setSubjectStats([]);
      setSelectedSubject(null);
      setClassGradeStats(null);
    }
  };

  // 重置筛选
  const resetFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
    setSubjectStats([]);
    setSelectedSubject(null);
    setClassGradeStats(null);
    setExamList([]);
    filterForm.resetFields();
  };

  // 打开新增/编辑模态框
  const openModal = (record = null) => {
    setEditingRecord(record);
    setModalVisible(true);

    if (record) {
      form.setFieldsValue({
        ...record,
        exam_date: record.exam_date ? moment(record.exam_date) : null,
      });
      if (record.class_id) {
        setSelectedClassId(record.class_id);
        fetchClassStudentsForModal(record.class_id); // 打开编辑时按需加载当前班级学生
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ full_score: 100 });
      setSelectedClassId(null);
      setFilteredStudents([]);
    }
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    setSelectedClassId(null);
    setFilteredStudents([]);
    form.resetFields();
  };

  // 打开批量导入模态框
  const openBatchModal = () => {
    setBatchModalVisible(true);
    setFileList([]);
    batchForm.resetFields();
  };

  // 关闭批量导入模态框
  const closeBatchModal = () => {
    setBatchModalVisible(false);
    setFileList([]);
    batchForm.resetFields();
  };

  // 查看详情
  const viewDetail = async (record) => {
    setSelectedRecord(record);
    setDetailDrawerVisible(true);
  };

  // 查看学生详细成绩
  const viewStudentDetail = async (studentId, studentName) => {
    const student = students.find(s => s.id === studentId) || { id: studentId, name: studentName };
    setSelectedStudent(student);
    setStudentDetailVisible(true);
    await fetchStudentGrades(studentId);
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      const schoolId = resolveOperationSchoolId({
        classes,
        classId: values.class_id || values.classId || editingRecord?.class_id,
        item: editingRecord,
        selectedSchoolId,
        user
      });
      const formData = {
        ...values,
        exam_date: values.exam_date ? values.exam_date.format('YYYY-MM-DD') : null,
        school_id: schoolId,
        // 自动计算得分率
        percentage: values.score && values.full_score ? (values.score / values.full_score) * 100 : null,
      };

      if (editingRecord) {
        await studentAPI.grades.updateGrade(editingRecord.id, formData);
        message.success('成绩记录更新成功');
      } else {
        await studentAPI.grades.createGrade(formData);
        message.success('成绩记录创建成功');
      }

      closeModal();
      fetchGrades();
    } catch (error) {
      message.error(editingRecord ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  // 批量导入成绩
  const handleBatchImport = async (values) => {
    try {
      if (fileList.length === 0) {
        message.error('请选择要导入的Excel文件');
        return;
      }

      const importData = {
        ...values,
        exam_date: values.exam_date.format('YYYY-MM-DD'),
        file: fileList[0]
      };

      const response = await studentAPI.grades.batchImport(importData);

      if (response.data.success) {
        const { imported_count, failed_count, total_count } = response.data.data;
        message.success(`批量导入完成！成功导入 ${imported_count} 条记录，失败 ${failed_count} 条，总计 ${total_count} 条。`);
        closeBatchModal();
        fetchGrades();
      } else {
        message.error(response.data.message || '批量导入失败');
      }
    } catch (error) {
      message.error('批量导入失败');
      console.error('批量导入失败:', error);
    }
  };

  // 删除记录
  const handleDelete = async (id) => {
    try {
      await studentAPI.grades.deleteGrade(id);
      message.success('删除成功');
      fetchGrades();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 文件上传处理
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // 下载模板
  const downloadTemplate = async () => {
    try {
      // 获取当前选中的班级ID
      const classId = filterForm.getFieldValue('classId');

      if (!classId) {
        message.warning('请先选择班级');
        return;
      }

      const response = await studentAPI.grades.downloadTemplate(classId);

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'grade_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error('下载模板失败');
    }
  };

  // 导出记录
  const exportRecords = async () => {
    try {
      const response = await studentAPI.grades.exportRecords({
        ...filters,
        schoolId: resolveOperationSchoolId({
          classes,
          classId: filters.classId || filters.class_id || selectedClassId,
          selectedSchoolId,
          user
        })
      });

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute('download', `grade_records_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 导出带等级的成绩报表
  const exportGradesWithLevel = async () => {
    if (!gradeLevelConfig) {
      message.warning('请先配置等级转换规则');
      return;
    }

    const classId = filterForm.getFieldValue('classId');
    const examType = filterForm.getFieldValue('examType');
    const examName = filterForm.getFieldValue('examName');

    if (!classId || !examType || !examName) {
      message.error('请先选择班级和考试');
      return;
    }

    try {
      message.loading('正在生成报表...', 0);

      // 获取所有学生成绩
      const response = await api.get('/student-grades/multi', {
        params: {
          classId,
          examType,
          examName,
          page: 1,
          pageSize: 1000
        }
      });

      if (response.data.success) {
        const allStudents = response.data.data || [];

        // 计算每个学生的等级
        const studentsWithLevel = allStudents.map((student, index) => {
          const score = parseFloat(student.total_score || 0);
          const rank = student.class_rank || (index + 1);
          const fullScore = Object.values(subjectFullScores).reduce((sum, s) => sum + (s || 0), 0) ||
            (student.subject_count || 1) * 100;

          const { level } = calculateGradeLevel(score, rank, allStudents.length, fullScore);

          return {
            座位号: student.seat_number || '',
            学生姓名: student.student_name || '',
            班级: student.class_name || '',
            考试类型: student.exam_type || '',
            考试名称: student.exam_name || '',
            总分: parseFloat(student.total_score || 0).toFixed(1),
            平均分: parseFloat(student.average_score || 0).toFixed(1),
            '等级(A-F)': level,
            班级排名: student.class_rank || '',
            语文: student.chinese ? parseFloat(student.chinese).toFixed(1) : '',
            数学: student.math ? parseFloat(student.math).toFixed(1) : '',
            英语: student.english ? parseFloat(student.english).toFixed(1) : '',
            物理: student.physics ? parseFloat(student.physics).toFixed(1) : '',
            化学: student.chemistry ? parseFloat(student.chemistry).toFixed(1) : '',
            生物: student.biology ? parseFloat(student.biology).toFixed(1) : '',
            历史: student.history ? parseFloat(student.history).toFixed(1) : '',
            地理: student.geography ? parseFloat(student.geography).toFixed(1) : '',
            政治: student.morality ? parseFloat(student.morality).toFixed(1) : '',
            体育: student.pe ? parseFloat(student.pe).toFixed(1) : '',
            考试日期: student.exam_date ? moment(student.exam_date).format('YYYY-MM-DD') : '',
          };
        });

        // 使用 xlsx 库生成 Excel 文件
        const XLSX = await import('xlsx');
        const worksheet = XLSX.utils.json_to_sheet(studentsWithLevel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '成绩报表');

        // 设置列宽
        const colWidths = [
          { wch: 8 },  // 座位号
          { wch: 12 }, // 学生姓名
          { wch: 15 }, // 班级
          { wch: 12 }, // 考试类型
          { wch: 20 }, // 考试名称
          { wch: 10 }, // 总分
          { wch: 10 }, // 平均分
          { wch: 12 }, // 等级(A-F)
          { wch: 10 }, // 班级排名
          { wch: 8 },  // 语文
          { wch: 8 },  // 数学
          { wch: 8 },  // 英语
          { wch: 8 },  // 物理
          { wch: 8 },  // 化学
          { wch: 8 },  // 生物
          { wch: 8 },  // 历史
          { wch: 8 },  // 地理
          { wch: 8 },  // 政治
          { wch: 8 },  // 体育
          { wch: 12 }, // 考试日期
        ];
        worksheet['!cols'] = colWidths;

        // 生成文件并下载
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const className = allStudents[0]?.class_name || '未知班级';
        link.setAttribute('download', `${className}_${examName}_成绩报表_含等级_${timestamp}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        message.destroy();
        message.success(`成功导出 ${studentsWithLevel.length} 条成绩记录`);
      }
    } catch (error) {
      message.destroy();
      console.error('导出失败:', error);
      message.error('导出失败: ' + (error.message || '未知错误'));
    }
  };

  // 获取成绩等级
  const getGradeLevel = (score) => {
    if (!score || isNaN(score)) return { level: '-', color: 'default' };
    if (score >= 90) return { level: '优秀', color: 'red' };
    if (score >= 80) return { level: '良好', color: 'orange' };
    if (score >= 70) return { level: '中等', color: 'yellow' };
    if (score >= 60) return { level: '及格', color: 'blue' };
    return { level: '不及格', color: 'gray' };
  };

  // 表格列定义 (多科目版本)
  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100,
      fixed: 'left',
      render: (name, record) => (
        <Button
          type="link"
          onClick={() => viewStudentDetail(record.student_id, name)}
          style={{ padding: 0, fontSize: '14px' }}
        >
          {name}
        </Button>
      ),
    },
    {
      title: '座位号',
      dataIndex: 'seat_number',
      key: 'seat_number',
      width: 80,
      sorter: true, // 启用服务器端排序
      defaultSortOrder: 'ascend', // 默认升序
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120,
    },
    {
      title: '考试类型',
      dataIndex: 'exam_type',
      key: 'exam_type',
      width: 100,
      render: (type) => {
        const typeOption = examTypes.find(t => t.value === type);
        return <Tag color={typeOption?.color}>{type}</Tag>;
      },
    },
    {
      title: '考试名称',
      dataIndex: 'exam_name',
      key: 'exam_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '总分',
      dataIndex: 'total_score',
      key: 'total_score',
      width: 80,
      render: (score) => {
        const numScore = parseFloat(score);
        return <Text strong>{!isNaN(numScore) ? numScore.toFixed(1) : '-'}</Text>;
      },
      sorter: true, // 启用服务器端排序
      defaultSortOrder: 'descend', // 默认降序（从高到低）
    },
    {
      title: '平均分',
      dataIndex: 'average_score',
      key: 'average_score',
      width: 80,
      render: (score) => {
        const numScore = parseFloat(score);
        return <Text>{!isNaN(numScore) ? numScore.toFixed(1) : '-'}</Text>;
      },
      sorter: true, // 启用服务器端排序
    },
    {
      title: '等级',
      dataIndex: 'average_score',
      key: 'grade_level',
      width: 80,
      render: (score) => {
        const numScore = parseFloat(score);
        const { level, color } = getGradeLevel(numScore);
        return <Tag color={color}>{level}</Tag>;
      },
    },
    {
      title: '等级(A-F)',
      dataIndex: 'total_score',
      key: 'grade_level_af',
      width: 90,
      render: (totalScore, record) => {
        if (!gradeLevelConfig) {
          return <Text type="secondary">未配置</Text>;
        }

        const score = parseFloat(totalScore);
        const rank = record.class_rank;
        const fullScore = Object.values(subjectFullScores).reduce((sum, s) => sum + (s || 0), 0) ||
          (record.subject_count || 1) * 100; // 估算满分

        // 这里需要获取班级总人数，暂时使用一个估算值
        // 在实际场景中，应该从后端获取或通过其他方式获取
        const totalStudents = grades.length || 1;

        const { level, color } = calculateGradeLevel(score, rank, totalStudents, fullScore);
        return <Tag color={color} style={{ fontSize: 14, fontWeight: 'bold' }}>{level}</Tag>;
      },
    },
    {
      title: '班级排名',
      dataIndex: 'class_rank',
      key: 'class_rank',
      width: 90,
      render: (rank) => rank ? <Badge count={rank} style={{ backgroundColor: '#52c41a' }} /> : '-',
      sorter: true, // 启用服务器端排序
      defaultSortOrder: 'ascend', // 默认升序（第1名到最后）
    },
    {
      title: '班级平均',
      dataIndex: 'class_average',
      key: 'class_average',
      width: 90,
      render: (avg) => {
        const numAvg = parseFloat(avg);
        return !isNaN(numAvg) ? <Text type="secondary">{numAvg.toFixed(1)}</Text> : '-';
      },
    },
    {
      title: '科目数',
      dataIndex: 'subject_count',
      key: 'subject_count',
      width: 80,
      render: (count) => <Tag>{count || 0}科</Tag>,
    },
    {
      title: '各科成绩',
      key: 'subjects',
      width: 300,
      ellipsis: true,
      render: (_, record) => {
        const subjects = [
          { name: '语', field: 'chinese', color: 'red' },
          { name: '数', field: 'math', color: 'blue' },
          { name: '英', field: 'english', color: 'green' },
          { name: '物', field: 'physics', color: 'purple' },
          { name: '化', field: 'chemistry', color: 'orange' },
          { name: '生', field: 'biology', color: 'cyan' },
          { name: '史', field: 'history', color: 'gold' },
          { name: '地', field: 'geography', color: 'lime' },
          { name: '政', field: 'morality', color: 'magenta' },
          { name: '体', field: 'pe', color: 'volcano' },
        ];

        return (
          <Space size={[4, 4]} wrap>
            {subjects.map(subject => {
              const score = record[subject.field];
              if (!score) return null;
              const numScore = parseFloat(score);
              return (
                <Tag key={subject.field} color={subject.color}>
                  {subject.name}:{numScore.toFixed(0)}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: '考试日期',
      dataIndex: 'exam_date',
      key: 'exam_date',
      width: 100,
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条成绩记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={filterForm} layout="inline" onFinish={handleFilter}>
          <Form.Item label="学校">
            <Select
              placeholder="选择学校"
              style={{ width: 180 }}
              value={selectedSchoolId}
              onChange={handleSchoolChange}
              disabled={shouldLockSchoolSelection(user, schools)}
              showSearch
              optionFilterProp="children"
            >
              {schools.map(school => (
                <Option key={school.id} value={normalizeScopeId(school.id)}>
                  {school.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="classId" label="班级" rules={[{ required: true, message: '请选择班级' }]}>
            <Select
              placeholder="请先选择班级"
              style={{ width: 150 }}
              allowClear
              onChange={handleFilterClassChange}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="exam" label="考试场次" rules={[{ required: true, message: '请选择考试' }]}>
            <Select
              placeholder="请选择考试"
              style={{ width: 250 }}
              allowClear
              onChange={handleExamChange}
              disabled={examList.length === 0}
            >
              {examList.map(exam => (
                <Option
                  key={`${exam.examType}_${exam.examName}`}
                  value={`${exam.examType}_${exam.examName}`}
                >
                  {exam.examType} - {exam.examName} ({moment(exam.examDate).format('YYYY-MM-DD')})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 隐藏字段用于存储考试类型和名称 */}
          <Form.Item name="examType" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="examName" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="studentId" label="学生">
            <Select
              placeholder="选择学生"
              style={{ width: 150 }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {students.map(student => (
                <Option key={student.id} value={student.id}>
                  {student.student_name || student.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="dateRange" label="考试时间">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                筛选
              </Button>
              <Button onClick={resetFilters}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 各科成绩统计仪表盘 */}
      {subjectStats.length > 0 && (
        <Card title="班级各科成绩统计" style={{ marginBottom: 16 }}>
          {/* 科目选择器 */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Text strong>选择科目:</Text>
              </Col>
              {subjectStats.map(stat => (
                <Col key={stat.field}>
                  <Card
                    size="small"
                    style={{
                      minWidth: 100,
                      cursor: 'pointer',
                      border: selectedSubject?.field === stat.field ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      backgroundColor: selectedSubject?.field === stat.field ? '#e6f7ff' : '#fff'
                    }}
                    onClick={() => setSelectedSubject(stat)}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {stat.subject}
                      </div>
                      {stat.field !== 'total' && stat.fullScore && (
                        <div style={{ marginTop: 8 }}>
                          <Space.Compact style={{ width: '100%' }}>
                            <Input
                              size="small"
                              style={{ width: 60 }}
                              value={subjectFullScores[stat.field] || stat.fullScore}
                              onChange={(e) => {
                                const score = parseFloat(e.target.value);
                                if (!isNaN(score) && score > 0) {
                                  setSubjectFullScores(prev => ({
                                    ...prev,
                                    [stat.field]: score
                                  }));
                                }
                              }}
                              placeholder="满分"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Button
                              size="small"
                              type="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newFullScore = subjectFullScores[stat.field] || stat.fullScore;
                                handleFullScoreChange(stat.field, newFullScore);
                              }}
                            >
                              确定
                            </Button>
                          </Space.Compact>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                            满分
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {/* 选中科目的详细统计 */}
          {selectedSubject && (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5} style={{ marginBottom: 0 }}>
                  {selectedSubject.subject} - 成绩分析
                  {selectedSubject.fullScore && (
                    <Text type="secondary" style={{ fontSize: 14, marginLeft: 8 }}>
                      (满分: {subjectFullScores[selectedSubject.field] || selectedSubject.fullScore} 分)
                    </Text>
                  )}
                </Title>
                <Button
                  type="primary"
                  icon={<StarOutlined />}
                  onClick={openGradeLevelConfig}
                >
                  等级配置
                </Button>
              </div>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8} md={6}>
                  <Card size="small">
                    <Statistic
                      title="参考人数"
                      value={selectedSubject.totalStudents}
                      prefix={<BookOutlined />}
                      suffix="人"
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card size="small">
                    <Statistic
                      title="平均分"
                      value={selectedSubject.avgScore}
                      precision={1}
                      prefix={<BarChartOutlined style={{ color: '#52c41a' }} />}
                      valueStyle={{ color: '#52c41a' }}
                      suffix="分"
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card size="small">
                    <Statistic
                      title="最高分"
                      value={selectedSubject.maxScore}
                      precision={1}
                      prefix={<TrophyOutlined style={{ color: '#f5222d' }} />}
                      valueStyle={{ color: '#f5222d' }}
                      suffix="分"
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card size="small">
                    <Statistic
                      title="最低分"
                      value={selectedSubject.minScore}
                      precision={1}
                      prefix={<StarOutlined style={{ color: '#faad14' }} />}
                      valueStyle={{ color: '#faad14' }}
                      suffix="分"
                    />
                  </Card>
                </Col>
              </Row>

              {/* 三率统计 */}
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Card size="small">
                    <Statistic
                      title={
                        <div>
                          <div>优秀率 (得分率≥80%)</div>
                          {selectedSubject.fullScore && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              (≥{((subjectFullScores[selectedSubject.field] || selectedSubject.fullScore) * 0.8).toFixed(0)}分)
                            </Text>
                          )}
                        </div>
                      }
                      value={selectedSubject.excellentRate}
                      precision={1}
                      prefix={<TrophyOutlined style={{ color: '#f5222d' }} />}
                      valueStyle={{ color: '#f5222d' }}
                      suffix="%"
                    />
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedSubject.excellentCount} 人
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewRateDetail('excellent')}
                        style={{ marginLeft: 8, padding: 0 }}
                      >
                        查看详情
                      </Button>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small">
                    <Statistic
                      title={
                        <div>
                          <div>良好率 (得分率≥70%)</div>
                          {selectedSubject.fullScore && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              (≥{((subjectFullScores[selectedSubject.field] || selectedSubject.fullScore) * 0.7).toFixed(0)}分)
                            </Text>
                          )}
                        </div>
                      }
                      value={selectedSubject.goodRate}
                      precision={1}
                      prefix={<StarOutlined style={{ color: '#fa8c16' }} />}
                      valueStyle={{ color: '#fa8c16' }}
                      suffix="%"
                    />
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedSubject.goodCount} 人
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewRateDetail('good')}
                        style={{ marginLeft: 8, padding: 0 }}
                      >
                        查看详情
                      </Button>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small">
                    <Statistic
                      title={
                        <div>
                          <div>及格率 (得分率≥60%)</div>
                          {selectedSubject.fullScore && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              (≥{((subjectFullScores[selectedSubject.field] || selectedSubject.fullScore) * 0.6).toFixed(0)}分)
                            </Text>
                          )}
                        </div>
                      }
                      value={selectedSubject.passRate}
                      precision={1}
                      prefix={<StarOutlined style={{ color: '#52c41a' }} />}
                      valueStyle={{ color: '#52c41a' }}
                      suffix="%"
                    />
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedSubject.passCount} 人
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewRateDetail('pass')}
                        style={{ marginLeft: 8, padding: 0 }}
                      >
                        查看详情
                      </Button>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* 等级分布统计 */}
              {gradeLevelConfig && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5} style={{ marginBottom: 16 }}>
                    等级分布统计
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                      (基于{gradeLevelConfig.type === 'score' ? '分数' : '排名'}的等级划分)
                    </Text>
                  </Title>

                  {levelStatsLoading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Text type="secondary">加载中...</Text>
                    </div>
                  ) : currentLevelStats ? (
                    <Row gutter={16}>
                      {['A', 'B', 'C', 'D', 'E', 'F'].map(level => {
                        const levelColors = {
                          A: '#f5222d',
                          B: '#fa8c16',
                          C: '#faad14',
                          D: '#1890ff',
                          E: '#13c2c2',
                          F: '#8c8c8c'
                        };

                        const count = currentLevelStats.stats[level] || 0;
                        const percentage = currentLevelStats.totalStudents > 0
                          ? ((count / currentLevelStats.totalStudents) * 100).toFixed(1)
                          : 0;

                        return (
                          <Col xs={12} sm={8} md={4} key={level}>
                            <Card
                              size="small"
                              style={{
                                backgroundColor: count > 0 ? `${levelColors[level]}10` : '#fafafa',
                                borderColor: levelColors[level]
                              }}
                            >
                              <Statistic
                                title={
                                  <div style={{ color: levelColors[level], fontWeight: 'bold' }}>
                                    {level} 等级
                                  </div>
                                }
                                value={count}
                                suffix="人"
                                valueStyle={{ color: levelColors[level], fontSize: 24 }}
                              />
                              <div style={{ marginTop: 8, textAlign: 'center' }}>
                                <Progress
                                  percent={parseFloat(percentage)}
                                  size="small"
                                  strokeColor={levelColors[level]}
                                  format={() => `${percentage}%`}
                                />
                              </div>
                              {count > 0 && (
                                <div style={{ marginTop: 8, textAlign: 'center' }}>
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => handleViewGradeLevelDetail(level)}
                                    style={{ padding: 0 }}
                                  >
                                    查看详情
                                  </Button>
                                </div>
                              )}
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Text type="secondary">暂无数据</Text>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* 班级整体三率统计（基于得分率） */}
      {classGradeStats && (
        <Card title="班级整体统计（基于总得分率）" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="参考人数"
                value={classGradeStats.total_students}
                prefix={<BookOutlined />}
                suffix="人"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="优秀率 (≥80%)"
                value={classGradeStats.excellent_rate}
                precision={1}
                valueStyle={{ color: '#f5222d' }}
                prefix={<TrophyOutlined />}
                suffix="%"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="良好率 (≥70%)"
                value={classGradeStats.good_rate}
                precision={1}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<StarOutlined />}
                suffix="%"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="及格率 (≥60%)"
                value={classGradeStats.pass_rate}
                precision={1}
                valueStyle={{ color: '#52c41a' }}
                prefix={<StarOutlined />}
                suffix="%"
              />
            </Col>
          </Row>
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              班级平均: {classGradeStats.class_average ? parseFloat(classGradeStats.class_average).toFixed(1) : '0.0'} 分 |
              最高分: {classGradeStats.highest_score ? parseFloat(classGradeStats.highest_score).toFixed(1) : '0.0'} |
              最低分: {classGradeStats.lowest_score ? parseFloat(classGradeStats.lowest_score).toFixed(1) : '0.0'}
            </Text>
          </div>
        </Card>
      )}

      {/* 操作区域和表格 */}
      <Card title="成绩记录管理">
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              录入成绩
            </Button>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={openBatchModal}
            >
              批量导入
            </Button>
          </Space>

          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模板
            </Button>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item key="1" icon={<HistoryOutlined />} onClick={exportRecords}>
                    导出基础记录
                  </Menu.Item>
                  <Menu.Item
                    key="2"
                    icon={<StarOutlined />}
                    onClick={exportGradesWithLevel}
                    disabled={!gradeLevelConfig}
                  >
                    导出含等级报表
                    {!gradeLevelConfig && <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(需先配置等级)</Text>}
                  </Menu.Item>
                </Menu>
              }
              placement="bottomRight"
            >
              <Button icon={<DownloadOutlined />}>
                导出 <DownloadOutlined />
              </Button>
            </Dropdown>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={grades}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑成绩记录' : '录入学生成绩'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="班级"
                rules={[{ required: true, message: '请选择班级' }]}
              >
                <Select
                  placeholder="请先选择班级"
                  value={selectedClassId}
                  onChange={handleClassChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="student_id"
                label="学生"
                rules={[{ required: true, message: '请选择学生' }]}
              >
                <Select
                  placeholder={selectedClassId ? "选择学生" : "请先选择班级"}
                  disabled={!selectedClassId}
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => {
                    const student = filteredStudents.find(s => s.id === value);
                    if (student) {
                      form.setFieldsValue({
                        student_name: student.student_name || student.name,
                        class_id: student.class_id,
                      });
                    }
                  }}
                >
                  {filteredStudents.map(student => (
                    <Option key={student.id} value={student.id}>
                      {student.student_name || student.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="student_name" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="class_id" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="subject"
                label="科目"
                rules={[{ required: true, message: '请选择科目' }]}
              >
                <Select placeholder="选择科目">
                  {subjects.map(subject => (
                    <Option key={subject.value} value={subject.value}>
                      <Tag color={subject.color}>{subject.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="exam_type"
                label="考试类型"
                rules={[{ required: true, message: '请选择考试类型' }]}
              >
                <Select placeholder="选择考试类型">
                  {examTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="exam_name" label="考试名称">
            <Input placeholder="请输入考试名称" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="score"
                label="得分"
                rules={[{ required: true, message: '请输入得分' }]}
              >
                <Input type="number" min={0} placeholder="得分" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="full_score"
                label="满分"
                rules={[{ required: true, message: '请输入满分' }]}
              >
                <Input type="number" min={1} placeholder="满分" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="exam_date" label="考试日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择考试日期" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rank_class" label="班级排名">
                <Input type="number" min={1} placeholder="班级排名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rank_grade" label="年级排名">
                <Input type="number" min={1} placeholder="年级排名" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="class_average" label="班级平均分">
                <Input type="number" step="0.1" placeholder="班级平均分" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="grade_average" label="年级平均分">
                <Input type="number" step="0.1" placeholder="年级平均分" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="comments" label="教师评语">
            <Input.TextArea rows={3} placeholder="请输入教师评语" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRecord ? '更新' : '录入'}
              </Button>
              <Button onClick={closeModal}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入成绩"
        open={batchModalVisible}
        onCancel={closeBatchModal}
        footer={null}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchImport}
        >
          <Alert
            message="批量导入说明"
            description="请先下载模板文件，按照模板格式填写学生成绩数据，然后上传Excel文件进行批量导入。"
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="exam_type"
                label="考试类型"
                rules={[{ required: true, message: '请选择考试类型' }]}
              >
                <Select placeholder="选择考试类型">
                  {examTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="class_id"
                label="班级"
                rules={[{ required: true, message: '请选择班级' }]}
              >
                <Select placeholder="选择班级">
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="exam_name"
            label="考试名称"
            rules={[{ required: true, message: '请输入考试名称' }]}
          >
            <Input placeholder="请输入考试名称" />
          </Form.Item>

          <Form.Item
            name="exam_date"
            label="考试日期"
            rules={[{ required: true, message: '请选择考试日期' }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="选择考试日期" />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Title level={5}>各科满分设置</Title>
            <Alert
              message="提示"
              description="请设置各科满分，默认100分。初中英语可能是120分，高中语数英可能是150分。用于计算得分率和三率统计。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item name="chinese_full" label="语文满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="math_full" label="数学满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="english_full" label="英语满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="physics_full" label="物理满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="chemistry_full" label="化学满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="biology_full" label="生物满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="history_full" label="历史满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="geography_full" label="地理满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="morality_full" label="道法满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="pe_full" label="体育满分" initialValue={100}>
                  <Input type="number" placeholder="100" suffix="分" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Form.Item label="上传Excel文件">
            <Upload
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={() => false} // 阻止自动上传
              accept=".xlsx,.xls"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>选择Excel文件</Button>
            </Upload>
            <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
              支持 .xlsx 和 .xls 格式，文件大小不超过10MB
            </Text>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                开始导入
              </Button>
              <Button onClick={closeBatchModal}>取消</Button>
              <Button type="link" onClick={downloadTemplate} icon={<DownloadOutlined />}>
                下载模板
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 成绩详情抽屉 */}
      <Drawer
        title="成绩记录详情"
        placement="right"
        onClose={() => setDetailDrawerVisible(false)}
        open={detailDrawerVisible}
        width={800}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="学生姓名">
                <Text strong>{selectedRecord.student_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="座位号">
                {selectedRecord.seat_number}
              </Descriptions.Item>
              <Descriptions.Item label="班级" span={2}>
                {selectedRecord.class_name}
              </Descriptions.Item>
              <Descriptions.Item label="考试类型">
                {(() => {
                  const type = examTypes.find(t => t.value === selectedRecord.exam_type);
                  return <Tag color={type?.color}>{selectedRecord.exam_type}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="考试名称">
                {selectedRecord.exam_name}
              </Descriptions.Item>
              <Descriptions.Item label="总分">
                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                  {parseFloat(selectedRecord.total_score || 0).toFixed(1)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="平均分">
                <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
                  {parseFloat(selectedRecord.average_score || 0).toFixed(1)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="等级">
                {(() => {
                  const { level, color } = getGradeLevel(parseFloat(selectedRecord.average_score));
                  return <Tag color={color} style={{ fontSize: '14px' }}>{level}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="科目数">
                <Tag color="blue">{selectedRecord.subject_count || 0}科</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="班级排名">
                {selectedRecord.class_rank ? (
                  <Badge count={selectedRecord.class_rank} style={{ backgroundColor: '#52c41a' }} />
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="班级平均分">
                {selectedRecord.class_average ? parseFloat(selectedRecord.class_average).toFixed(1) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="考试日期">
                {moment(selectedRecord.exam_date).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="录入教师">
                {selectedRecord.teacher_name || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* 各科成绩明细 */}
            <div style={{ marginTop: '24px' }}>
              <Title level={5}>各科成绩</Title>
              <Row gutter={[16, 16]}>
                {[
                  { name: '语文', field: 'chinese', color: '#f5222d' },
                  { name: '数学', field: 'math', color: '#1890ff' },
                  { name: '英语', field: 'english', color: '#52c41a' },
                  { name: '物理', field: 'physics', color: '#722ed1' },
                  { name: '化学', field: 'chemistry', color: '#fa8c16' },
                  { name: '生物', field: 'biology', color: '#13c2c2' },
                  { name: '历史', field: 'history', color: '#faad14' },
                  { name: '地理', field: 'geography', color: '#a0d911' },
                  { name: '道德与法治', field: 'morality', color: '#eb2f96' },
                  { name: '体育', field: 'pe', color: '#fa541c' },
                ].map(subject => {
                  const score = selectedRecord[subject.field];
                  if (!score) return null;

                  const numScore = parseFloat(score);
                  return (
                    <Col span={8} key={subject.field}>
                      <Card size="small">
                        <Statistic
                          title={subject.name}
                          value={numScore}
                          precision={1}
                          valueStyle={{ color: subject.color }}
                          suffix="分"
                        />
                        <Progress
                          percent={numScore}
                          size="small"
                          strokeColor={subject.color}
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>

            {selectedRecord.comments && (
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>教师评语</Title>
                <Card>
                  <Paragraph>{selectedRecord.comments}</Paragraph>
                </Card>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* 学生详细成绩模态框 */}
      <Modal
        title={`${selectedStudent?.name} - 各科详细成绩`}
        open={studentDetailVisible}
        onCancel={() => setStudentDetailVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedStudent && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="总科目数"
                    value={studentGrades.length}
                    prefix={<BookOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="平均分"
                    value={studentGrades.reduce((sum, g) => sum + g.score, 0) / (studentGrades.length || 1)}
                    precision={1}
                    prefix={<BarChartOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="最高分"
                    value={Math.max(...studentGrades.map(g => g.score))}
                    prefix={<TrophyOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="最低分"
                    value={Math.min(...studentGrades.map(g => g.score))}
                    prefix={<StarOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Tabs defaultActiveKey="list">
              <TabPane tab="成绩列表" key="list">
                <Table
                  dataSource={studentGrades}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '科目',
                      dataIndex: 'subject',
                      key: 'subject',
                      render: (subject) => {
                        const subjectOption = subjects.find(s => s.value === subject);
                        return <Tag color={subjectOption?.color}>{subject}</Tag>;
                      },
                    },
                    {
                      title: '考试类型',
                      dataIndex: 'exam_type',
                      key: 'exam_type',
                      render: (type) => {
                        const typeOption = examTypes.find(t => t.value === type);
                        return <Tag color={typeOption?.color}>{type}</Tag>;
                      },
                    },
                    {
                      title: '分数',
                      dataIndex: 'score',
                      key: 'score',
                      render: (score, record) => (
                        <Space direction="vertical" size={0}>
                          <Text strong>{score}/{record.full_score}</Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {record.percentage?.toFixed(1)}%
                          </Text>
                        </Space>
                      ),
                    },
                    {
                      title: '等级',
                      dataIndex: 'percentage',
                      key: 'grade_level',
                      render: (percentage) => {
                        const { level, color } = getGradeLevel(percentage);
                        return <Tag color={color}>{level}</Tag>;
                      },
                    },
                    {
                      title: '班级排名',
                      dataIndex: 'rank_class',
                      key: 'rank_class',
                      render: (rank) => (
                        <Badge count={rank} style={{ backgroundColor: '#52c41a' }} />
                      ),
                    },
                    {
                      title: '考试日期',
                      dataIndex: 'exam_date',
                      key: 'exam_date',
                      render: (date) => moment(date).format('YYYY-MM-DD'),
                    },
                  ]}
                />
              </TabPane>
              <TabPane tab="成绩分析" key="analysis">
                <Row gutter={16}>
                  {subjects.map(subject => {
                    const subjectGrades = studentGrades.filter(g => g.subject === subject.value);
                    if (subjectGrades.length === 0) return null;

                    const avgScore = subjectGrades.reduce((sum, g) => sum + g.score, 0) / subjectGrades.length;
                    const { level } = getGradeLevel(avgScore);

                    return (
                      <Col span={8} key={subject.value} style={{ marginBottom: 16 }}>
                        <Card size="small">
                          <Statistic
                            title={subject.label}
                            value={avgScore}
                            precision={1}
                            valueStyle={{ color: subject.color }}
                            prefix={<Tag color={subject.color}>{level}</Tag>}
                          />
                          <Progress
                            percent={avgScore}
                            size="small"
                            strokeColor={subject.color}
                            showInfo={false}
                          />
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* 三率详情抽屉 */}
      <Drawer
        title={
          <div>
            <Text strong>{rateDetailData.subject} - </Text>
            <Text type="secondary">
              {rateDetailData.type === 'excellent' && '优秀学生名单'}
              {rateDetailData.type === 'good' && '良好学生名单'}
              {rateDetailData.type === 'pass' && '及格学生名单'}
            </Text>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              (满分: {rateDetailData.fullScore}分)
            </Text>
          </div>
        }
        width={720}
        open={rateDetailVisible}
        onClose={() => setRateDetailVisible(false)}
        extra={
          <Space>
            <Text type="secondary">共 {rateDetailData.students?.length || 0} 人</Text>
          </Space>
        }
      >
        <Table
          dataSource={rateDetailData.students}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          columns={[
            {
              title: '排名',
              key: 'rank',
              width: 60,
              render: (_, __, index) => (
                <Badge
                  count={index + 1}
                  style={{
                    backgroundColor: index < 3 ? '#f5222d' : '#1890ff'
                  }}
                />
              )
            },
            {
              title: '座位号',
              dataIndex: 'seat_number',
              width: 80
            },
            {
              title: '学生姓名',
              dataIndex: 'student_name',
              width: 120,
              render: (name) => <Text strong>{name}</Text>
            },
            {
              title: '分数',
              dataIndex: 'displayScore',
              width: 100,
              render: (score) => (
                <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                  {score}
                </Text>
              )
            },
            {
              title: '得分率',
              dataIndex: 'rate',
              width: 100,
              render: (rate) => (
                <div>
                  <Text strong style={{ color: '#52c41a' }}>{rate}%</Text>
                  <Progress
                    percent={parseFloat(rate)}
                    size="small"
                    showInfo={false}
                    strokeColor={
                      parseFloat(rate) >= 80 ? '#f5222d' :
                        parseFloat(rate) >= 70 ? '#fa8c16' :
                          '#52c41a'
                    }
                  />
                </div>
              )
            },
            {
              title: '等级',
              key: 'level',
              width: 80,
              render: (_, record) => {
                const rate = parseFloat(record.rate);
                let level = '及格';
                let color = '#52c41a';
                if (rate >= 80) {
                  level = '优秀';
                  color = '#f5222d';
                } else if (rate >= 70) {
                  level = '良好';
                  color = '#fa8c16';
                }
                return <Tag color={color}>{level}</Tag>;
              }
            },
            {
              title: '班级排名',
              dataIndex: 'class_rank',
              width: 100,
              render: (rank) => {
                if (!rank) return <Text type="secondary">-</Text>;
                return (
                  <Badge
                    count={rank}
                    style={{
                      backgroundColor: rank <= 3 ? '#f5222d' :
                        rank <= 10 ? '#fa8c16' : '#1890ff'
                    }}
                  />
                );
              }
            }
          ]}
        />
      </Drawer>

      {/* 等级配置模态框 */}
      <Modal
        title="等级转换配置"
        open={gradeLevelConfigVisible}
        onCancel={() => setGradeLevelConfigVisible(false)}
        footer={null}
        width={800}
      >
        <Alert
          message="等级转换说明"
          description="您可以选择基于分数（得分率）或基于排名（百分比）来划分等级。配置后将应用于当前科目的等级统计。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={gradeLevelConfigForm}
          layout="vertical"
          onFinish={handleSaveGradeLevelConfig}
          initialValues={{
            type: 'score'
          }}
        >
          <Form.Item
            name="type"
            label="等级划分方式"
            rules={[{ required: true, message: '请选择等级划分方式' }]}
          >
            <Select
              onChange={(value) => setGradeLevelType(value)}
              style={{ width: '100%' }}
            >
              <Option value="score">基于分数（得分率）</Option>
              <Option value="rank">基于排名（前百分比）</Option>
            </Select>
          </Form.Item>

          {/* 基于分数的配置 */}
          {gradeLevelType === 'score' && (
            <>
              <Alert
                message="分数划分规则"
                description="设置各等级的最低得分率（百分比）。例如：A等级最低90%表示得分率≥90%的学生为A等级。"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="A_min"
                    label="A 等级最低得分率 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="90" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="B_min"
                    label="B 等级最低得分率 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="80" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="C_min"
                    label="C 等级最低得分率 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="70" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="D_min"
                    label="D 等级最低得分率 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="60" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="E_min"
                    label="E 等级最低得分率 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="50" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <div style={{ paddingTop: 30 }}>
                    <Text type="secondary">F 等级：低于 E 等级的所有学生</Text>
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* 基于排名的配置 */}
          {gradeLevelType === 'rank' && (
            <>
              <Alert
                message="排名划分规则"
                description="设置各等级占总人数的百分比。例如：A等级10%表示排名前10%的学生为A等级。"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="A_percent"
                    label="A 等级占比 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="10" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="B_percent"
                    label="B 等级占比 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="20" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="C_percent"
                    label="C 等级占比 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="30" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="D_percent"
                    label="D 等级占比 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="20" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="E_percent"
                    label="E 等级占比 (%)"
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <Input type="number" min={0} max={100} placeholder="15" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <div style={{ paddingTop: 30 }}>
                    <Text type="secondary">F 等级：剩余的所有学生</Text>
                  </div>
                </Col>
              </Row>
            </>
          )}

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                保存配置
              </Button>
              <Button onClick={() => setGradeLevelConfigVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 等级详情抽屉 */}
      <Drawer
        title={
          <div>
            <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
              {gradeLevelDetailData.level} 等级学生名单
            </Text>
            <Text type="secondary" style={{ marginLeft: 12, fontSize: 14 }}>
              {gradeLevelDetailData.subject}
            </Text>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              (满分: {gradeLevelDetailData.fullScore}分)
            </Text>
          </div>
        }
        width={720}
        open={gradeLevelDetailVisible}
        onClose={() => setGradeLevelDetailVisible(false)}
        extra={
          <Space>
            <Text type="secondary">共 {gradeLevelDetailData.students?.length || 0} 人</Text>
          </Space>
        }
      >
        <Table
          dataSource={gradeLevelDetailData.students}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          columns={[
            {
              title: '排名',
              key: 'rank',
              width: 60,
              render: (_, __, index) => (
                <Badge
                  count={index + 1}
                  style={{
                    backgroundColor: index < 3 ? '#f5222d' : '#1890ff'
                  }}
                />
              )
            },
            {
              title: '座位号',
              dataIndex: 'seat_number',
              width: 80
            },
            {
              title: '学生姓名',
              dataIndex: 'student_name',
              width: 120,
              render: (name) => <Text strong>{name}</Text>
            },
            {
              title: '分数',
              dataIndex: 'displayScore',
              width: 100,
              render: (score) => (
                <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                  {score}
                </Text>
              )
            },
            {
              title: '得分率',
              dataIndex: 'percentage',
              width: 100,
              render: (percentage) => (
                <div>
                  <Text strong style={{ color: '#52c41a' }}>{percentage}%</Text>
                  <Progress
                    percent={parseFloat(percentage)}
                    size="small"
                    showInfo={false}
                    strokeColor="#52c41a"
                  />
                </div>
              )
            },
            {
              title: '班级排名',
              dataIndex: 'class_rank',
              width: 100,
              render: (rank) => {
                if (!rank) return <Text type="secondary">-</Text>;
                return (
                  <Badge
                    count={rank}
                    style={{
                      backgroundColor: rank <= 3 ? '#f5222d' :
                        rank <= 10 ? '#fa8c16' : '#1890ff'
                    }}
                  />
                );
              }
            }
          ]}
        />
      </Drawer>
    </div>
  );
};

export default StudentGrades;
